import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';
import type {
  CodeWithName,
  GrpoLookups,
  GrpoPoLine,
  GrpoPostingResult,
  GrpoPostingRow,
  GrpoProjectOption,
  GrpoPurchaseOrder,
  OpenPoSummary,
} from '../types/grpo';

/**
 * GRPO (Goods Receipt PO): find an open Purchase Order by project, vendor and/or
 * PO number, enter the quantities received on its open lines, and post a Goods
 * Receipt PO to SAP. Lines whose warehouse type / item class call for it are
 * issued right after by an automatic Goods Issue (decided by the server).
 * Everything shown is read live from SAP for the selected company.
 */

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtDate = (s?: string | null): string => {
  if (!s) return '—';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const fmtDateTime = (s?: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : `${fmtDate(s)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fmtQty = (n: number) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
const fmtMoney = (n: number, currency?: string) =>
  `${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
const todayInRiyadh = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const newSubmissionKey = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* not a secure context */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};
const errorMessage = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;

/** A code with its description underneath (accounts, dimensions, projects, warehouses). */
const CodeName: React.FC<{ value?: CodeWithName | null; extra?: React.ReactNode }> = ({ value, extra }) => {
  if (!value?.code) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{value.code}</Typography>
      {value.name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{value.name}</Typography>
      )}
      {extra}
    </Box>
  );
};

const STATUS_LABEL: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  Completed: { label: 'Completed', color: 'success' },
  GoodsIssueFailed: { label: 'Goods Issue failed', color: 'error' },
  Posted: { label: 'GRPO posted', color: 'info' },
  Posting: { label: 'Posting', color: 'warning' },
  Failed: { label: 'Failed', color: 'error' },
};
const GI_LABEL: Record<string, string> = {
  NotRequired: 'Not required',
  Pending: 'Pending',
  Posted: 'Posted',
  Failed: 'Failed',
};

export default function GoodsReceiptPO(): JSX.Element {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const [tab, setTab] = useState(0);

  // Lookups
  const [lookups, setLookups] = useState<GrpoLookups | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Search
  const [project, setProject] = useState<GrpoProjectOption | null>(null);
  const [vendor, setVendor] = useState<CodeWithName | null>(null);
  const [vendorInput, setVendorInput] = useState('');
  const [vendorOptions, setVendorOptions] = useState<CodeWithName[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [docNum, setDocNum] = useState('');
  const [results, setResults] = useState<OpenPoSummary[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selected PO
  const [po, setPo] = useState<GrpoPurchaseOrder | null>(null);
  const [poLoading, setPoLoading] = useState(false);
  const [poError, setPoError] = useState<string | null>(null);
  const [poNotice, setPoNotice] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<number, string>>({});
  // Reporting Date = SAP posting (and due) date; Invoice Ref Date = SAP document date. Both default to today.
  const [reportingDate, setReportingDate] = useState(todayInRiyadh());
  const [invoiceRefDate, setInvoiceRefDate] = useState(todayInRiyadh());
  const [vendorRef, setVendorRef] = useState('');
  const [remarks, setRemarks] = useState('');

  // Posting
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<GrpoPostingResult | null>(null);
  const [retrying, setRetrying] = useState(false);
  // Kept across attempts whose outcome is unknown (no response), so sending
  // again can never create a second GRPO; replaced after any answer.
  const submissionKey = useRef<string | null>(null);

  // History
  const [history, setHistory] = useState<{ rows: GrpoPostingRow[]; total: number }>({ rows: [], total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_LIMIT = 25;

  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    apiService.getGrpoLookups(companyId)
      .then((res: any) => { if (alive) setLookups(res?.data ?? null); })
      .catch((e) => { if (alive) setLookupError(errorMessage(e, 'Failed to load projects and dimensions')); });
    return () => { alive = false; };
  }, [companyId]);

  // Vendor search (live SAP), debounced.
  useEffect(() => {
    if (!companyId) return undefined;
    const term = vendorInput.trim();
    if (vendor && term === `${vendor.code} — ${vendor.name}`) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      setVendorLoading(true);
      apiService.searchGrpoVendors(companyId, term)
        .then((res: any) => { if (alive) setVendorOptions(Array.isArray(res?.data) ? res.data : []); })
        .catch(() => { if (alive) setVendorOptions([]); })
        .finally(() => { if (alive) setVendorLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [companyId, vendorInput, vendor]);

  const runSearch = useCallback(async () => {
    if (!companyId) return;
    const n = docNum.trim();
    if (n && !/^\d+$/.test(n)) {
      setSearchError('PO number must contain digits only');
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      const res: any = await apiService.searchGrpoPurchaseOrders({
        companyId,
        projectCode: project?.code || undefined,
        cardCode: vendor?.code || undefined,
        docNum: n || undefined,
      });
      setResults(Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setTruncated(!!res?.data?.truncated);
    } catch (e) {
      setResults(null);
      setSearchError(errorMessage(e, 'Search failed'));
    } finally {
      setSearching(false);
    }
  }, [companyId, docNum, project, vendor]);

  const clearSearch = () => {
    setProject(null);
    setVendor(null);
    setVendorInput('');
    setDocNum('');
    setResults(null);
    setSearchError(null);
  };

  const loadPo = useCallback(async (docEntry: number, keepInputs = false) => {
    if (!companyId) return;
    setPoLoading(true);
    setPoError(null);
    setPoNotice(null);
    try {
      const res: any = await apiService.getGrpoPurchaseOrder(companyId, docEntry);
      setPo(res?.data ?? null);
      setQty({});
      if (!keepInputs) {
        setReportingDate(todayInRiyadh());
        setInvoiceRefDate(todayInRiyadh());
        setVendorRef('');
        setRemarks('');
      }
      submissionKey.current = null;
    } catch (e: any) {
      setPo(null);
      // 409: closed or fully received — information, not a failure.
      if (Number(e?.response?.status) === 409) setPoNotice(errorMessage(e, 'Nothing left to receive'));
      else setPoError(errorMessage(e, 'Failed to load the Purchase Order'));
    } finally {
      setPoLoading(false);
    }
  }, [companyId]);

  const loadHistory = useCallback(async () => {
    if (!companyId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res: any = await apiService.getGrpoHistory({
        companyId, status: historyStatus || undefined, q: historyQuery.trim() || undefined, page: historyPage, limit: HISTORY_LIMIT,
      });
      setHistory({ rows: res?.data?.rows ?? [], total: Number(res?.data?.total ?? 0) });
    } catch (e) {
      setHistoryError(errorMessage(e, 'Failed to load the GRPO history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [companyId, historyStatus, historyQuery, historyPage]);

  useEffect(() => {
    if (tab === 1) void loadHistory();
  }, [tab, loadHistory]);

  // Quantities entered, validated per line.
  const entered = useMemo(() => {
    const lines: Array<{ line: GrpoPoLine; quantity: number }> = [];
    const errors: Record<number, string> = {};
    for (const line of po?.lines ?? []) {
      const raw = (qty[line.lineNum] ?? '').trim();
      if (!raw) continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) errors[line.lineNum] = 'Enter a positive number';
      else if (n > line.openQty + 1e-9) errors[line.lineNum] = `More than the open ${fmtQty(line.openQty)}`;
      else if (n > 0) lines.push({ line, quantity: n });
    }
    return { lines, errors, valid: Object.keys(errors).length === 0 && lines.length > 0 };
  }, [po, qty]);
  const issueLines = entered.lines.filter((l) => l.line.goodsIssue.required);
  const receiptValue = entered.lines.reduce((sum, l) => sum + l.quantity * l.line.unitPrice, 0);

  const receiveAllOpen = () => {
    const next: Record<number, string> = {};
    for (const line of po?.lines ?? []) if (!line.blockedReason) next[line.lineNum] = String(line.openQty);
    setQty(next);
  };

  const openConfirm = () => {
    setSubmitError(null);
    if (!submissionKey.current) submissionKey.current = newSubmissionKey();
    setConfirmOpen(true);
  };

  const submit = async () => {
    if (!po || !entered.valid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res: any = await apiService.submitGrpo({
        companyId,
        poDocEntry: po.docEntry,
        submissionKey: submissionKey.current || newSubmissionKey(),
        docDate: reportingDate,
        taxDate: invoiceRefDate,
        vendorRef: vendorRef.trim() || undefined,
        remarks: remarks.trim() || undefined,
        lines: entered.lines.map((l) => ({ lineNum: l.line.lineNum, quantity: l.quantity })),
      });
      submissionKey.current = null;
      setConfirmOpen(false);
      setResult(res?.data ?? null);
    } catch (e: any) {
      // Only a definite rejection (4xx) settles the outcome. After a timeout, a
      // gateway/server error (5xx) or "busy" (409) the GRPO may exist or still
      // be posting: keep the key, so sending again returns that GRPO.
      const status = Number(e?.response?.status || 0);
      const definite = status >= 400 && status < 500 && status !== 409;
      if (definite) submissionKey.current = null;
      setSubmitError(definite
        ? errorMessage(e, 'GRPO posting failed')
        : `${errorMessage(e, 'No answer from the server.')} The GRPO may have been created: press Post to SAP again to check — it will not be posted twice.`);
    } finally {
      setSubmitting(false);
    }
  };

  const closeResult = () => {
    const posted = result?.grpoDocEntry;
    setResult(null);
    if (posted && po) {
      // Refresh open quantities; a fully received PO no longer loads.
      void loadPo(po.docEntry);
      void runSearch();
    }
  };

  const retryGoodsIssue = async (postingId: number, fromHistory = false) => {
    setRetrying(true);
    try {
      const res: any = await apiService.retryGrpoGoodsIssue(companyId, postingId);
      if (fromHistory) {
        await loadHistory();
        setResult(res?.data ?? null);
      } else {
        setResult(res?.data ?? null);
      }
    } catch (e) {
      const message = errorMessage(e, 'Goods Issue retry failed');
      if (fromHistory) setHistoryError(message);
      else setResult((prev) => (prev ? { ...prev, error: message } : prev));
    } finally {
      setRetrying(false);
    }
  };

  const dims = po?.dimensions ?? lookups?.dimensions ?? [];

  const resultColumns: ResponsiveTableColumn<OpenPoSummary>[] = [
    { id: 'docNum', header: 'PO No.', cell: (r) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.docNum}</Typography> },
    { id: 'docDate', header: 'Date', cell: (r) => fmtDate(r.docDate) },
    { id: 'due', header: 'Delivery', cell: (r) => fmtDate(r.docDueDate), hideOnMobile: true },
    { id: 'vendor', header: 'Vendor', cell: (r) => <CodeName value={{ code: r.cardCode, name: r.cardName }} /> },
    {
      id: 'projects', header: 'Project(s)', cell: (r) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {(r.projects.length ? r.projects : ['—']).map((p) => <Chip key={p} size="small" label={p} variant="outlined" />)}
        </Stack>
      ),
    },
    { id: 'total', header: 'Total', align: 'right', cell: (r) => fmtMoney(r.docTotal, r.currency) },
    {
      id: 'type', header: 'Type', hideOnMobile: true, cell: (r) => (r.docType && r.docType !== 'dDocument_Items'
        ? <Chip size="small" color="warning" label="Service" />
        : <Chip size="small" label="Items" variant="outlined" />),
    },
  ];

  // Service lines (SAP holds the amount as Quantity x 1.00) also show the PO's service terms.
  const hasServiceLines = !!po?.lines.some((l) => l.service);
  const lineColumns: ResponsiveTableColumn<GrpoPoLine>[] = [
    { id: 'line', header: '#', width: 40, cell: (l) => l.lineNum + 1 },
    {
      id: 'item', header: 'Item', cell: (l) => (
        <Box sx={{ minWidth: 160 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.itemCode || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{l.itemDescription}</Typography>
          {l.itemClass && <Typography variant="caption" color="text.secondary">Class: {l.itemClass}</Typography>}
          {l.service && <Chip size="small" label="Service" color="info" variant="outlined" sx={{ ml: l.itemClass ? 1 : 0, height: 20 }} />}
        </Box>
      ),
    },
    {
      id: 'warehouse', header: 'Warehouse', cell: (l) => (
        <CodeName value={l.warehouse} extra={l.warehouse.type
          ? <Chip size="small" label={l.warehouse.type} variant="outlined" sx={{ mt: 0.25, height: 20 }} />
          : undefined}
        />
      ),
    },
    { id: 'ordered', header: 'Ordered', align: 'right', cell: (l) => `${fmtQty(l.orderedQty)} ${l.uomCode}` },
    { id: 'received', header: 'Received so far', align: 'right', hideOnMobile: true, cell: (l) => fmtQty(l.receivedQty) },
    { id: 'open', header: 'Open', align: 'right', cell: (l) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtQty(l.openQty)}</Typography> },
    {
      id: 'qty', header: 'Receive now', width: 130, cell: (l) => (l.blockedReason
        ? <Tooltip title={l.blockedReason}><Chip size="small" color="warning" label="Receive in SAP" /></Tooltip>
        : (
          <TextField
            size="small"
            type="number"
            value={qty[l.lineNum] ?? ''}
            onChange={(e) => setQty((prev) => ({ ...prev, [l.lineNum]: e.target.value }))}
            error={!!entered.errors[l.lineNum]}
            helperText={entered.errors[l.lineNum]}
            inputProps={{ min: 0, max: l.openQty, step: 'any', inputMode: 'decimal', 'aria-label': `Quantity received for line ${l.lineNum + 1}` }}
            sx={{ width: 120 }}
          />
        )),
    },
    { id: 'price', header: 'Unit price', align: 'right', hideOnMobile: true, cell: (l) => fmtMoney(l.unitPrice, l.currency) },
    ...(hasServiceLines ? [
      { id: 'serviceQty', header: 'Service qty', align: 'right', cell: (l) => (l.service ? fmtQty(l.service.qty) : '—') },
      { id: 'servicePrice', header: 'Service unit price', align: 'right', cell: (l) => (l.service ? fmtMoney(l.service.unitPrice, l.currency) : '—') },
    ] as ResponsiveTableColumn<GrpoPoLine>[] : []),
    { id: 'project', header: 'Project', cell: (l) => <CodeName value={l.project} /> },
    ...dims.map((d): ResponsiveTableColumn<GrpoPoLine> => ({
      id: `dim${d.code}`,
      header: d.label,
      cell: (l) => <CodeName value={l.dimensions?.[d.code]} />,
    })),
    { id: 'account', header: 'G/L account', cell: (l) => <CodeName value={l.account} /> },
    {
      id: 'gi', header: 'Auto Goods Issue', cell: (l) => (
        <Tooltip title={l.goodsIssue.reason}>
          <Chip
            size="small"
            color={l.goodsIssue.required ? 'primary' : 'default'}
            variant={l.goodsIssue.required ? 'filled' : 'outlined'}
            label={l.goodsIssue.required ? 'Yes' : 'No'}
          />
        </Tooltip>
      ),
    },
  ];

  const historyColumns: ResponsiveTableColumn<GrpoPostingRow>[] = [
    { id: 'date', header: 'Posted', cell: (r) => fmtDateTime(r.createdDate) },
    { id: 'po', header: 'PO No.', cell: (r) => r.poDocNum || r.poDocEntry },
    { id: 'vendor', header: 'Vendor', cell: (r) => <CodeName value={{ code: r.cardCode || '', name: r.cardName || '' }} /> },
    { id: 'projects', header: 'Project(s)', hideOnMobile: true, cell: (r) => r.projectCodes.join(', ') || '—' },
    { id: 'grpo', header: 'GRPO No.', cell: (r) => r.grpoDocNum || '—' },
    {
      id: 'gi', header: 'Goods Issue', cell: (r) => (r.goodsIssueDocNum
        ? r.goodsIssueDocNum
        : (r.goodsIssueStatus ? GI_LABEL[r.goodsIssueStatus] || r.goodsIssueStatus : '—')),
    },
    {
      id: 'status', header: 'Status', cell: (r) => {
        const s = STATUS_LABEL[r.status] || { label: r.status, color: 'default' as const };
        return <Chip size="small" color={s.color} label={s.label} />;
      },
    },
    { id: 'by', header: 'By', hideOnMobile: true, cell: (r) => r.createdByName || '—' },
    {
      id: 'note', header: 'Message', cell: (r) => (
        <Box sx={{ maxWidth: 360 }}>
          {r.error && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{r.error}</Typography>}
          {r.warnings.map((w) => <Typography key={w} variant="caption" color="warning.main" sx={{ display: 'block' }}>{w}</Typography>)}
          {r.status === 'GoodsIssueFailed' && (
            <Button size="small" variant="outlined" disabled={retrying} onClick={() => void retryGoodsIssue(r.postingId, true)} sx={{ mt: 0.5 }}>
              Retry Goods Issue
            </Button>
          )}
        </Box>
      ),
    },
  ];

  if (!companyId) {
    return <Box sx={{ p: 2 }}><Alert severity="warning">Select a company first.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <MoveToInboxIcon color="primary" />
        <Typography variant="h5" component="h1">GRPO — Goods Receipt PO</Typography>
      </Stack>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }} aria-label="GRPO sections">
        <Tab label="Receive" />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          {lookupError && <Alert severity="error">{lookupError}</Alert>}

          {!po && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Find an open Purchase Order</Typography>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={lookups?.projects ?? []}
                    value={project}
                    onChange={(_e, v) => setProject(v)}
                    getOptionLabel={(o) => `${o.code}${o.name ? ` — ${o.name}` : ''}${o.active ? '' : ' (inactive)'}`}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    loading={!lookups && !lookupError}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Project"
                        helperText={lookups && !lookups.allProjects ? 'Your assigned projects' : undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={vendorOptions}
                    value={vendor}
                    onChange={(_e, v) => setVendor(v)}
                    inputValue={vendorInput}
                    onInputChange={(_e, v) => setVendorInput(v)}
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => `${o.code} — ${o.name}`}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    loading={vendorLoading}
                    noOptionsText={vendorInput.trim() ? 'No vendor found' : 'Type a vendor code or name'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Business Partner (vendor)"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {vendorLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="PO number"
                    value={docNum}
                    onChange={(e) => setDocNum(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(); }}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                      onClick={() => void runSearch()}
                      disabled={searching}
                      fullWidth
                    >
                      Search
                    </Button>
                    <Button variant="outlined" onClick={clearSearch} aria-label="Clear search" sx={{ minWidth: 44 }}>
                      <ClearIcon fontSize="small" />
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Open Purchase Orders are read live from SAP. Pick a project, a vendor and/or a PO number, then select the PO to receive.
              </Typography>
            </Paper>
          )}

          {!po && searchError && <Alert severity="error">{searchError}</Alert>}
          {!po && results && (
            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {results.length} open Purchase Order{results.length === 1 ? '' : 's'}{truncated ? ' (first 100 — narrow the search)' : ''}
              </Typography>
              <ResponsiveTable
                ariaLabel="Open Purchase Orders"
                columns={resultColumns}
                rows={results}
                getRowKey={(r) => r.docEntry}
                onRowClick={(r) => void loadPo(r.docEntry)}
                emptyMessage="No open Purchase Order matches this search"
              />
            </Paper>
          )}

          {poLoading && (
            <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Loading the Purchase Order from SAP…</Typography></Stack>
          )}
          {poError && <Alert severity="error">{poError}</Alert>}
          {poNotice && <Alert severity="info" onClose={() => setPoNotice(null)}>{poNotice}</Alert>}

          {po && (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="h6" component="h2">Purchase Order {po.docNum}</Typography>
                    <Typography variant="body2" color="text.secondary">{po.cardCode} — {po.cardName}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<RefreshIcon />} onClick={() => void loadPo(po.docEntry, true)} disabled={poLoading}>Reload</Button>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => { setPo(null); setPoError(null); }}>Back to search</Button>
                  </Stack>
                </Stack>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">PO date</Typography><Typography variant="body2">{fmtDate(po.docDate)}</Typography></Grid>
                  <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Delivery date</Typography><Typography variant="body2">{fmtDate(po.docDueDate)}</Typography></Grid>
                  <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">PO total</Typography><Typography variant="body2">{fmtMoney(po.docTotal, po.currency)}</Typography></Grid>
                  <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Project</Typography><CodeName value={po.project} /></Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Dimensions</Typography>
                <Grid container spacing={1.5}>
                  {po.headerDimensions.map((d) => (
                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={d.dimension}>
                      <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                      {d.source === 'multiple'
                        ? <Typography variant="body2" color="text.secondary">Varies by line</Typography>
                        : <CodeName value={{ code: d.code, name: d.name }} />}
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      label="Reporting Date"
                      value={reportingDate}
                      onChange={(e) => setReportingDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: todayInRiyadh() }}
                      helperText="SAP Posting Date"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      label="Invoice Ref Date"
                      value={invoiceRefDate}
                      onChange={(e) => setInvoiceRefDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: todayInRiyadh() }}
                      helperText="SAP Document Date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField size="small" fullWidth label="Vendor ref. (delivery note no.)" value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} inputProps={{ maxLength: 100 }} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField size="small" fullWidth label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} inputProps={{ maxLength: 200 }} />
                  </Grid>
                </Grid>
                {po.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mt: 1.5 }}>{w}</Alert>)}
                {po.hiddenLineCount > 0 && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    {po.hiddenLineCount} open line{po.hiddenLineCount === 1 ? '' : 's'} of this PO belong{po.hiddenLineCount === 1 ? 's' : ''} to projects outside your access and {po.hiddenLineCount === 1 ? 'is' : 'are'} not shown.
                  </Alert>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Open lines ({po.lines.length})</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<DoneAllIcon />} onClick={receiveAllOpen}>Receive all open</Button>
                    <Button size="small" onClick={() => setQty({})}>Clear quantities</Button>
                  </Stack>
                </Stack>
                <ResponsiveTable
                  ariaLabel="Open Purchase Order lines"
                  columns={lineColumns}
                  rows={po.lines}
                  getRowKey={(l) => l.lineNum}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {entered.lines.length} line{entered.lines.length === 1 ? '' : 's'} to receive · {fmtMoney(receiptValue, po.currency)}
                    {issueLines.length > 0 && ` · ${issueLines.length} will be issued automatically`}
                  </Typography>
                  <Button variant="contained" size="large" startIcon={<MoveToInboxIcon />} disabled={!entered.valid || submitting} onClick={openConfirm}>
                    Create GRPO
                  </Button>
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              select
              label="Status"
              value={historyStatus}
              onChange={(e) => { setHistoryStatus(e.target.value); setHistoryPage(1); }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_LABEL).map(([value, s]) => <MenuItem key={value} value={value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField
              size="small"
              label="PO / GRPO / Goods Issue / vendor"
              value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setHistoryPage(1); void loadHistory(); } }}
              sx={{ flex: 1 }}
            />
            <Button startIcon={<RefreshIcon />} onClick={() => void loadHistory()} disabled={historyLoading}>Refresh</Button>
          </Stack>
          {historyError && <Alert severity="error" sx={{ mb: 1 }}>{historyError}</Alert>}
          {historyLoading ? (
            <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
          ) : (
            <ResponsiveTable
              ariaLabel="GRPO history"
              columns={historyColumns}
              rows={history.rows}
              getRowKey={(r) => r.postingId}
              emptyMessage="No GRPO posted from this screen yet"
            />
          )}
          {history.total > HISTORY_LIMIT && (
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }}>
              <Button size="small" disabled={historyPage <= 1} onClick={() => setHistoryPage((p) => p - 1)}>Previous</Button>
              <Typography variant="body2">Page {historyPage} of {Math.ceil(history.total / HISTORY_LIMIT)}</Typography>
              <Button size="small" disabled={historyPage * HISTORY_LIMIT >= history.total} onClick={() => setHistoryPage((p) => p + 1)}>Next</Button>
            </Stack>
          )}
        </Paper>
      )}

      <ResponsiveDialog open={confirmOpen} onClose={() => { if (!submitting) setConfirmOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Post GRPO to SAP</DialogTitle>
        <DialogContent dividers>
          {po && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Purchase Order <b>{po.docNum}</b> · {po.cardCode} — {po.cardName} · reporting date <b>{fmtDate(reportingDate)}</b> · invoice ref date <b>{fmtDate(invoiceRefDate)}</b>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {entered.lines.map(({ line, quantity }) => (
                  <li key={line.lineNum}>
                    <Typography variant="body2">
                      Line {line.lineNum + 1}: {line.itemCode} {line.itemDescription} — <b>{fmtQty(quantity)} {line.uomCode}</b> into {line.warehouse.code}
                      {line.service && ` · service ${fmtQty(line.service.qty)} × ${fmtMoney(line.service.unitPrice, line.currency)}`}
                      {line.goodsIssue.required && <Chip size="small" color="primary" label="Auto Goods Issue" sx={{ ml: 1, height: 20 }} />}
                    </Typography>
                  </li>
                ))}
              </Box>
              {issueLines.length > 0 ? (
                <Alert severity="info">
                  After the GRPO, a Goods Issue will be created for {issueLines.length} line{issueLines.length === 1 ? '' : 's'} from the same
                  warehouse, project and dimensions, with the PO number in U_GL_PONumber. SAP determines its G/L account.
                </Alert>
              ) : (
                <Alert severity="info">No received line needs an automatic Goods Issue.</Alert>
              )}
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={() => void submit()} disabled={submitting || !entered.valid}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <MoveToInboxIcon />}>
            {submitting ? 'Posting…' : 'Post to SAP'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!result} onClose={closeResult} maxWidth="md" fullWidth>
        <DialogTitle>
          {result?.status === 'Completed' ? 'GRPO posted' : result?.status === 'GoodsIssueFailed' ? 'GRPO posted — Goods Issue failed' : 'GRPO'}
        </DialogTitle>
        <DialogContent dividers>
          {result && (
            <Stack spacing={1.5}>
              {result.replayed && <Alert severity="info">This submission had already been posted; showing its result.</Alert>}
              <Typography variant="body1">
                GRPO <b>{result.grpoDocNum ?? '—'}</b> created in SAP against PO {result.poDocNum}.
              </Typography>
              {result.goodsIssueStatus === 'Posted' && (
                <Typography variant="body1">Goods Issue <b>{result.goodsIssueDocNum}</b> created automatically.</Typography>
              )}
              {result.goodsIssueStatus === 'NotRequired' && (
                <Typography variant="body2" color="text.secondary">No line needed an automatic Goods Issue.</Typography>
              )}
              {result.goodsIssueLines.length > 0 && (
                <ResponsiveTable
                  ariaLabel="Goods Issue lines"
                  columns={[
                    { id: 'item', header: 'Item', cell: (l) => l.itemCode },
                    { id: 'qty', header: 'Quantity', align: 'right', cell: (l) => fmtQty(l.quantity) },
                    { id: 'wh', header: 'Warehouse', cell: (l) => l.warehouseCode },
                    { id: 'prj', header: 'Project', cell: (l) => l.projectCode || '—' },
                    { id: 'acct', header: 'G/L account', cell: (l) => <CodeName value={l.account} /> },
                  ]}
                  rows={result.goodsIssueLines}
                  getRowKey={(l) => l.lineNum}
                />
              )}
              {result.warnings.map((w) => <Alert key={w} severity="warning">{w}</Alert>)}
              {result.error && (
                <Alert severity="error">
                  {result.status === 'GoodsIssueFailed' ? `The GRPO is in SAP, but the Goods Issue failed: ${result.error}` : result.error}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {result?.status === 'GoodsIssueFailed' && (
            <Button variant="outlined" disabled={retrying} onClick={() => void retryGoodsIssue(result.postingId)}
              startIcon={retrying ? <CircularProgress size={16} /> : <RefreshIcon />}>
              Retry Goods Issue
            </Button>
          )}
          <Button variant="contained" onClick={closeResult}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
}
